import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Box, TextField, Alert, CircularProgress, Button, Chip, Stack } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * SquarePaymentForm - Credit Card Tokenization Component
 * 
 * This component uses Square Web Payments SDK to securely collect credit card info
 * and tokenize it (convert to a secure token) without your server ever touching raw card data.
 * 
 * HOW IT WORKS:
 * 1. Square SDK creates a secure iframe for card input
 * 2. User enters card details (card never touches your server)
 * 3. Square converts card to a token (e.g., "cnon:card-abc123")
 * 4. Token is passed to parent component via onTokenGenerated callback
 * 5. Parent component saves token with the ride data
 * 
 * IMPORTANT: You MUST set your Square Application ID in environment config!
 */

const SquarePaymentForm = forwardRef(({ appId: propAppId, locationId: propLocationId, onTokenGenerated, onTokenCleared, onError }, ref) => {
    const [payments, setPayments] = useState(null);
    const [card, setCard] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isSaving, setIsSaving] = useState(false);
    const [savedToken, setSavedToken] = useState(null);

    // Holds the Square payments instance across retries (ref avoids re-creating it)
    const paymentsRef = useRef(null);
    const isMountedRef = useRef(true);

    // Expose reset function to parent via ref
    useImperativeHandle(ref, () => ({
        reset: () => {
            setSavedToken(null);
            setError(null);
        },
        clearCard: () => {
            setSavedToken(null);
            if (onTokenCleared) {
                onTokenCleared();
            }
        }
    }));

    // Square credentials: prefer props from server, fall back to env vars
    const SQUARE_APPLICATION_ID = propAppId || import.meta.env.VITE_SQUARE_APPLICATION_ID;
    const SQUARE_LOCATION_ID = propLocationId || import.meta.env.VITE_SQUARE_LOCATION_ID;

    // Track mounted state and clean up on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            paymentsRef.current = null;
        };
    }, []);

    // Effect 1: Load Square SDK script
    useEffect(() => {
        if (!SQUARE_APPLICATION_ID || SQUARE_APPLICATION_ID === 'your-square-application-id') {
            setError('Square Application ID not configured! Ensure the server has SquareSettings:ApplicationId set.');
            setIsLoading(false);
            return;
        }

        if (!window.Square) {
            const existingScript = document.querySelector('script[src="https://web.squarecdn.com/v1/square.js"]');
            if (existingScript) {
                existingScript.addEventListener('load', () => { if (isMountedRef.current) setIsLoading(false); });
                existingScript.addEventListener('error', () => {
                    if (isMountedRef.current) {
                        setError('Square SDK failed to load. Please refresh the page.');
                        setIsLoading(false);
                    }
                });
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://web.squarecdn.com/v1/square.js';
            script.onload = () => { if (isMountedRef.current) setIsLoading(false); };
            script.onerror = () => {
                if (isMountedRef.current) {
                    setError('Square SDK failed to load. Please refresh the page.');
                    setIsLoading(false);
                }
            };
            document.head.appendChild(script);
            return;
        }

        setIsLoading(false);
    }, []);

    // Effect 2: Initialize Square after SDK is loaded and container is visible
    useEffect(() => {
        if (isLoading || payments) {
            return;
        }

        const timer = setTimeout(() => {
            initializeSquare();
        }, 500);

        return () => clearTimeout(timer);
    }, [isLoading, payments]);

    const initializeSquare = async (retryCount = 0) => {
        if (!isMountedRef.current) return;

        const MAX_RETRIES = 4;
        const RETRY_DELAYS = [1000, 2000, 3000, 5000];

        try {
            // Create payments instance only once; reuse on retries
            if (!paymentsRef.current) {
                paymentsRef.current = window.Square.payments(SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID);
            }

            const cardInstance = await paymentsRef.current.card();
            await cardInstance.attach('#card-container');

            if (!isMountedRef.current) return;
            // Only update state after COMPLETE success
            setPayments(paymentsRef.current);
            setCard(cardInstance);
        } catch (err) {
            if (!isMountedRef.current) return;
            const isInitTimeout = String(err.message).includes('unable to be initialized in time');
            if (isInitTimeout && retryCount < MAX_RETRIES) {
                // If payments() itself failed, reset so it's tried again on next retry
                if (!paymentsRef.current) {
                    // payments() threw — will retry on next attempt
                }
                const delay = RETRY_DELAYS[retryCount];
                console.warn(`⚠️ Square not ready, retrying in ${delay}ms (${retryCount + 1}/${MAX_RETRIES})...`);
                setTimeout(() => initializeSquare(retryCount + 1), delay);
                return;
            }
            console.error('❌ Failed to initialize Square:', err);
            paymentsRef.current = null;
            setError('Failed to load payment form. Please refresh the page.');
            if (onError) {
                onError(err.message || 'Failed to initialize payment form');
            }
        }
    };

    const tokenizeCard = async () => {
        if (!card) {
            const error = 'Payment form not initialized';
            setError(error);
            if (onError) onError(error);
            return null;
        }

        try {
            setError(null);
            setIsSaving(true);

            const result = await card.tokenize();

            if (result.status === 'OK') {
                // Save the token for display
                setSavedToken(result.token);

                // Pass token to parent component
                if (onTokenGenerated) {
                    onTokenGenerated(result.token);
                }

                return result.token;
            } else {
                const errorMessage = result.errors?.[0]?.message || 'Unable to process card';
                console.error('❌ Tokenization failed:', errorMessage);
                setError(errorMessage);

                if (onError) {
                    onError(errorMessage);
                }

                return null;
            }
        } catch (err) {
            const errorMessage = err.message || 'An error occurred processing the card';
            console.error('❌ Tokenization error:', err);
            setError(errorMessage);

            if (onError) {
                onError(errorMessage);
            }

            return null;
        } finally {
            setIsSaving(false);
        }
    };

    // Handle save card button click
    const handleSaveCard = async () => {
        await tokenizeCard();
    };

    // Expose tokenize function to parent via ref or callback
    useEffect(() => {
        if (card) {
            // Make tokenize function available globally (for parent to call)
            window.squareTokenizeCard = tokenizeCard;
        }

        return () => {
            // Clean up on unmount
            if (window.squareTokenizeCard) {
                delete window.squareTokenizeCard;
            }
        };
    }, [card]);

    // Handle removing the saved card
    const handleRemoveCard = () => {
        setSavedToken(null);
        if (onTokenCleared) {
            onTokenCleared();
        }
    };

    // If card is already saved, show success state with remove option
    if (savedToken) {
        return (
            <Box sx={{ my: 2 }}>
                <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{ mb: 2 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <span>Card saved successfully</span>
                        <Chip label="Prepaid" size="small" color="success" />
                    </Stack>
                </Alert>
                <Box sx={{
                    p: 2,
                    bgcolor: 'success.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon color="success" />
                        <span>Card on file will be charged when the ride is completed</span>
                    </Box>
                    <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveCard}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Remove Card
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ my: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {(!SQUARE_APPLICATION_ID || SQUARE_APPLICATION_ID === 'your-square-application-id') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    ⚠️ Square Application ID not configured!
                    Please set VITE_SQUARE_APPLICATION_ID in your environment.
                </Alert>
            )}

            {/* Square Card Form Container - Square SDK renders card inputs here */}
            <Box
                id="card-container"
                sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    padding: 2,
                    minHeight: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& iframe': {
                        border: 'none',
                        width: '100%'
                    }
                }}
            >
                {!card && !error && (
                    <CircularProgress size={24} />
                )}
            </Box>

            {/* Save Card Button */}
            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSaveCard}
                disabled={!card || isSaving}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
                sx={{ mt: 2 }}
            >
                {isSaving ? 'Saving Card...' : 'Save Card'}
            </Button>
        </Box>
    );
});

export default SquarePaymentForm;
